import * as appointmentTypes from 'modules/appointments/actionTypes';
import * as campaignFilesTypes from 'modules/campaignFiles/actionTypes';
import * as campaignTypes from 'modules/campaigns/actionTypes';
import * as types from 'modules/checklists/actionTypes';
import { MULTIPLE_FETCH_SUCCESS } from 'modules/checklists/actionTypes';
import type {
  ToggleOpenChecklistAction,
  OpenChecklistAction,
  OpenChecklistsAction,
  CloseChecklistAction,
  StoredChecklist,
} from 'modules/checklists/types';
import * as interventionTypes from 'modules/interventions/actionTypes';
import { uniq } from 'utils/ObjectUtils';
import { createReducer } from 'utils/ReduxUtils';

export type Slice = string[];

type EntitySuccessPayload = {
  checklists?: StoredChecklist[];
  selfServices?: Array<{ checklists?: StoredChecklist[] }>;
};

const initialState: Slice = [];

const updateChecklists = (state: Slice, checklists: StoredChecklist[] = [], initial = false): Slice =>
  uniq(
    checklists.reduce<Slice>((acc, checklist) => {
      const isOpened = initial ? !!checklist.items : state.includes(checklist.id) || !!checklist.items;
      if (isOpened) {
        return [...acc, checklist.id];
      }
      return acc.filter(checklistId => checklist.id !== checklistId);
    }, state)
  );

const updateFromIntervention = (state: Slice, { payload: intervention }: { payload: EntitySuccessPayload }): Slice =>
  updateChecklists(state, intervention.checklists);

const updateFromAppointmentIntervention = (
  state: Slice,
  { payload: { intervention } }: { payload: { intervention: EntitySuccessPayload } }
): Slice => updateFromIntervention(state, { payload: intervention });

const updateFromAppointment = (state: Slice, { payload: appointment }: { payload: { intervention: EntitySuccessPayload } }): Slice => {
  const { intervention } = appointment;
  return updateFromIntervention(state, { payload: intervention });
};

const toggleOpen = (state: Slice, { payload: checklistId }: ToggleOpenChecklistAction): Slice => {
  const exists = state.includes(checklistId);
  return exists ? state.filter(id => checklistId !== id) : uniq([...state, checklistId]);
};

const open = (state: Slice, { payload: checklistId }: OpenChecklistAction): Slice => uniq([...state, checklistId]);

const openMultiple = (state: Slice, { payload: checklistIds }: OpenChecklistsAction): Slice => uniq([...state, ...checklistIds]);

const close = (state: Slice, { payload: checklistId }: CloseChecklistAction): Slice =>
  state.filter(id => id !== checklistId);

const closeMultiple = (state: Slice, { payload: checklistIds }: { payload: Array<string> }): Slice =>
  state.filter(checklistId => !checklistIds.includes(checklistId));

const closeAll = (): Slice => [];

const setFromChecklists = (state: Slice, { payload }: { payload: { content: StoredChecklist[] } }): Slice =>
  updateChecklists(state, payload.content);

const DEFAULT_OPEN_TYPE = [
  interventionTypes.FETCH_INTERVENTION_SUCCESS,
  campaignFilesTypes.FETCH_CAMPAIGN_FILE_SUCCESS,
  interventionTypes.IMPORT_AUDITS_OR_TSCANS_SUCCESS,
  interventionTypes.IMPORT_WORKS_INTO_INTERVENTION_SUCCESS,
];

const updateFromEntity = (state: Slice, { type, payload }: { type: string; payload?: EntitySuccessPayload }): Slice => {
  const initial = DEFAULT_OPEN_TYPE.includes(type);

  const extrasChecklists =
    payload?.selfServices?.reduce<StoredChecklist[]>((acc, { checklists }) => {
      if (checklists && checklists.length > 0) {
        acc.push(...checklists);
      }
      return acc;
    }, []) ?? [];

  const allChecklists = [...(payload?.checklists ?? []), ...extrasChecklists];
  return updateChecklists(state, allChecklists, initial);
};

const updateFromImportSuccess = (
  state: Slice,
  action: { type: string; payload: { intervention: EntitySuccessPayload } }
): Slice => {
  const { intervention } = action.payload;
  return updateFromEntity(state, { ...action, payload: intervention });
};

export default createReducer(
  initialState,
  {
    [campaignTypes.FETCH_CAMPAIGN_SUCCESS]: updateFromEntity,
    [campaignTypes.UPDATE_CAMPAIGN_SUCCESS]: updateFromEntity,
    [interventionTypes.FETCH_INTERVENTION_SUCCESS]: updateFromEntity,
    [interventionTypes.UPDATE_INTERVENTION_SUCCESS]: updateFromEntity,
    [interventionTypes.IMPORT_CHECKLISTS_SUCCESS]: updateFromEntity,
    [interventionTypes.IMPORT_TIRE_REPORT_SUCCESS]: updateFromEntity,
    [campaignFilesTypes.FETCH_CAMPAIGN_FILE_SUCCESS]: updateFromEntity,
    [interventionTypes.IMPORT_AUDITS_OR_TSCANS_SUCCESS]: updateFromImportSuccess,
    [interventionTypes.IMPORT_CHECKLISTS_GROUPS_SUCCESS]: updateFromEntity,
    [interventionTypes.IMPORT_WORKS_INTO_INTERVENTION_SUCCESS]: updateFromEntity,

    [appointmentTypes.FETCH_APPOINTMENT_SUCCESS]: updateFromAppointment,
    [appointmentTypes.FETCH_INTERVENTION_SUCCESS]: updateFromAppointmentIntervention,

    [types.OPEN_CHECKLIST]: open,
    [types.CLOSE_CHECKLIST]: close,
    [types.OPEN_CHECKLISTS]: openMultiple,
    [types.CLOSE_CHECKLISTS]: closeMultiple,
    [types.TOGGLE_OPEN_CHECKLIST]: toggleOpen,
    [types.CLOSE_ALL_CHECKLISTS]: closeAll,
  },
  [
    {
      matcher: ({ type, meta }) =>
        type === MULTIPLE_FETCH_SUCCESS && meta != null && ['campaign-import', 'extras-import'].includes(meta.source),
      reducer: setFromChecklists,
    },
  ]
);
